import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
} from "recharts";
import "./ventas.css";
import { ArrowUpRight, ArrowDownRight } from "lucide-react";

import {
  getVentasMensuales,
  getAcumuladoVentas,
  getVendedores,
  getProductosMensuales,
  getAcumuladoProductos,
} from "../../../Services/fetchVentas";

const CUR_YEAR = new Date().getFullYear();
const PREV_YEAR = CUR_YEAR - 1;
const COLORS = ["#6366f1", "#22d3ee"];

const months = ["01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12"];
const monthNames = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

export default function Ventas() {
  const [tab, setTab] = useState("ventas");
  const [users, setUsers] = useState([]);
  const [user, setUser] = useState({ eId: 0, gId: 0, nombre: "Todos" });
  const [data, setData] = useState({
    ventas: {
      cur: [],
      prev: [],
      acum: { curTodos: 0, curFin: 0, prevTodos: 0, prevFin: 0 },
    },
    productos: {
      cur: [],
      prev: [],
      acum: { curTodos: 0, curFin: 0, prevTodos: 0, prevFin: 0 },
    },
  });

  const MIN_VENTAS_MENSUALES = 2033333.2;
  const MIN_PRODUCTOS_MENSUALES = 50;

  /** 📊 Carga de datos */
  const loadData = useCallback(async () => {
    const { eId, gId } = user;
    try {
      const [vcRes, vpRes, acvRes, apvRes] = await Promise.all([
        getVentasMensuales(CUR_YEAR, gId, eId),
        getVentasMensuales(PREV_YEAR, gId, eId),
        getAcumuladoVentas(CUR_YEAR, gId, eId),
        getAcumuladoVentas(PREV_YEAR, gId, eId),
      ]);

      const [pcRes, ppRes, acpRes, appRes] = await Promise.all([
        getProductosMensuales(CUR_YEAR, gId, eId),
        getProductosMensuales(PREV_YEAR, gId, eId),
        getAcumuladoProductos(CUR_YEAR, gId, eId),
        getAcumuladoProductos(PREV_YEAR, gId, eId),
      ]);

      const vc = Array.isArray(vcRes.data) ? vcRes.data : [];
      const vp = Array.isArray(vpRes.data) ? vpRes.data : [];
      const acv = acvRes.data || {};
      const apv = apvRes.data || {};
      const pc = Array.isArray(pcRes.data) ? pcRes.data : [];
      const pp = Array.isArray(ppRes.data) ? ppRes.data : [];
      const acp = acpRes.data || {};
      const app = appRes.data || {};

      setData({
        ventas: {
          cur: vc,
          prev: vp,
          acum: {
            curTodos: Number(acv.suma_total_ventas_todos ?? 0),
            curFin: Number(acv.suma_total_ventas_finalizados ?? 0),
            prevTodos: Number(apv.suma_total_ventas_todos ?? 0),
            prevFin: Number(apv.suma_total_ventas_finalizados ?? 0),
          },
        },
        productos: {
          cur: pc,
          prev: pp,
          acum: {
            curTodos: Number(acp.suma_total_productos_todos ?? 0),
            curFin: Number(acp.suma_total_productos_finalizados ?? 0),
            prevTodos: Number(app.suma_total_productos_todos ?? 0),
            prevFin: Number(app.suma_total_productos_finalizados ?? 0),
          },
        },
      });
    } catch (err) {
      console.error("❌ Error cargando datos de ventas/productos", err);
    }
  }, [user]);

  /** 👥 Carga vendedores + data inicial */
  useEffect(() => {
    getVendedores()
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : [];
        setUsers([{ eId: 0, gId: 0, nombre: "Todos" }, ...list]);
      })
      .catch(console.error);

    loadData();
  }, [loadData]);

  /** 🔄 Crea datos comparativos */
  const comparativeData = useCallback(
    (section) => {
      const prevArray = Array.isArray(data[section].prev) ? data[section].prev : [];
      const curArray = Array.isArray(data[section].cur) ? data[section].cur : [];

      const todosKey = section === "ventas" ? "total_ventas_todos" : "suma_total_producto_todos";
      const finKey = section === "ventas" ? "total_ventas_finalizados" : "suma_total_producto_finalizados";

      return months.map((m) => ({
        dm: monthNames[+m - 1],
        prevTodos: Number(prevArray.find((x) => x.dm === m)?.[todosKey] ?? 0),
        prevFin: Number(prevArray.find((x) => x.dm === m)?.[finKey] ?? 0),
        curTodos: Number(curArray.find((x) => x.dm === m)?.[todosKey] ?? 0),
        curFin: Number(curArray.find((x) => x.dm === m)?.[finKey] ?? 0),
      }));
    },
    [data]
  );

  const comparativeDataMemo = useMemo(() => comparativeData(tab), [comparativeData, tab]);

  /** 🧮 Renderiza tabla */
  const renderTab = (section) => {
    const tableData = comparativeDataMemo;

    const vendorCount = Math.max(users.length - 1, 1);
    const baseMin = section === "ventas" ? MIN_VENTAS_MENSUALES : MIN_PRODUCTOS_MENSUALES;
    const effectiveMin = user.eId === 0 ? baseMin * vendorCount : baseMin;

    return (
      <>
        <div className="controls mb-3 d-flex align-items-center">
          <label className="me-2 mb-0" style={{ color: "#a3b3d1" }}>
            Vendedor:
          </label>
          <select
            className="form-select w-auto"
            value={user.eId}
            onChange={(e) => setUser(users.find((u) => u.eId === +e.target.value))}
            style={{
              background: "rgba(255,255,255,0.08)",
              border: "none",
              color: "#f0f5ff",
              minWidth: 140,
            }}
          >
            {users.map((u) => (
              <option
                key={`${u.eId}-${u.gId}`}
                value={u.eId}
                style={{ background: "#1f2a44", color: "#f0f5ff" }}
              >
                {u.nombre}
              </option>
            ))}
          </select>
        </div>

        <div className="table-wrapper">
          <div className="table-responsive mb-4">
            <table className="table">
              <thead>
                <tr>
                  <th>Mes</th>
                  <th>{PREV_YEAR} Todos</th>
                  <th>{PREV_YEAR} Finalizados</th>
                  <th>{CUR_YEAR} Todos</th>
                  <th>{CUR_YEAR} Finalizados</th>
                  <th>% Cambio</th>
                  <th>Meta</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {tableData.map((row) => {
                  const prevTodos = row.prevTodos;
                  const curTodos = row.curTodos;
                  const percent = prevTodos === 0 ? 0 : ((curTodos - prevTodos) / prevTodos) * 100;
                  const meetsThreshold = curTodos >= effectiveMin;
                  const deltaVsMetaPct =
                    effectiveMin > 0 ? ((curTodos - effectiveMin) / effectiveMin) * 100 : 0;

                  return (
                    <tr key={row.dm}>
                      <td>{row.dm}</td>
                      <td>
                        {section === "ventas"
                          ? `$${prevTodos.toLocaleString("en-US", { minimumFractionDigits: 2 })}`
                          : prevTodos.toLocaleString()}
                      </td>
                      <td>
                        {section === "ventas"
                          ? `$${row.prevFin.toLocaleString("en-US", { minimumFractionDigits: 2 })}`
                          : row.prevFin.toLocaleString()}
                      </td>
                      <td>
                        {section === "ventas"
                          ? `$${curTodos.toLocaleString("en-US", { minimumFractionDigits: 2 })}`
                          : curTodos.toLocaleString()}
                      </td>
                      <td>
                        {section === "ventas"
                          ? `$${row.curFin.toLocaleString("en-US", { minimumFractionDigits: 2 })}`
                          : row.curFin.toLocaleString()}
                      </td>
                      <td>
                        <span className={`percent-change ${percent >= 0 ? "percent-up" : "percent-down"}`}>
                          {percent >= 0 ? <ArrowUpRight size={16} /> : <ArrowDownRight size={16} />}
                          {Math.abs(percent).toFixed(2)}%
                        </span>
                      </td>
                      <td>
                        {section === "ventas"
                          ? `$${effectiveMin.toLocaleString("en-US", { minimumFractionDigits: 2 })}`
                          : effectiveMin.toLocaleString()}
                      </td>
                      <td>
                        <span className={`badge ${meetsThreshold ? "bg-success" : "bg-danger"}`}>
                          {meetsThreshold ? "Cumple" : "Bajo"}
                        </span>
                        <div
                          className={`status-detail ${
                            meetsThreshold ? "text-success" : "text-warning"
                          }`}
                        >
                          {meetsThreshold
                            ? `Cumplió con ${Math.abs(deltaVsMetaPct).toFixed(2)}% más`
                            : `Le faltó ${Math.abs(deltaVsMetaPct).toFixed(2)}%`}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* 📊 GRÁFICOS */}
        <div className="row">
          <div className="col-12 col-md-6 mb-4">
            <div className="chart-card">
              <h5>
                {section === "ventas" ? "Ventas" : "Productos"} Mensual ({PREV_YEAR} vs {CUR_YEAR})
              </h5>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={tableData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                  <XAxis dataKey="dm" tick={{ fill: "#a3b3d1" }} />
                  <YAxis
                    tickFormatter={(v) =>
                      section === "ventas"
                        ? `$${v.toLocaleString("en-US", { minimumFractionDigits: 2 })}`
                        : v.toLocaleString()
                    }
                    tick={{ fill: "#a3b3d1" }}
                  />
                  <ReferenceLine y={effectiveMin} stroke="#ff9800" strokeDasharray="3 3" label="Meta" />
                  <Tooltip
                    formatter={(v) =>
                      section === "ventas"
                        ? `$${v.toLocaleString("en-US", { minimumFractionDigits: 2 })}`
                        : v.toLocaleString()
                    }
                    contentStyle={{
                      backgroundColor: "#1f324f",
                      border: "none",
                      borderRadius: 8,
                    }}
                    itemStyle={{ color: "#f0f5ff" }}
                  />
                  <Legend wrapperStyle={{ color: "#f0f5ff" }} />
                  <Bar dataKey="prevTodos" fill={COLORS[0]} name={`${PREV_YEAR} Todos`} />
                  <Bar dataKey="curTodos" fill={COLORS[1]} name={`${CUR_YEAR} Todos`} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="col-12 col-md-6 mb-4">
            <div className="chart-card">
              <h5>Acumulado {section === "ventas" ? "Ventas" : "Productos"}</h5>
              {(() => {
                const a = data[section].acum;
                const accumData = [
                  { name: `${PREV_YEAR} Todos`, value: a.prevTodos },
                  { name: `${PREV_YEAR} Fin`, value: a.prevFin },
                  { name: `${CUR_YEAR} Todos`, value: a.curTodos },
                  { name: `${CUR_YEAR} Fin`, value: a.curFin },
                ];
                return (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={accumData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                      <XAxis dataKey="name" tick={{ fill: "#a3b3d1" }} />
                      <YAxis
                        tickFormatter={(v) =>
                          section === "ventas"
                            ? `$${v.toLocaleString("en-US", { minimumFractionDigits: 2 })}`
                            : v.toLocaleString()
                        }
                        tick={{ fill: "#a3b3d1" }}
                      />
                      <Tooltip
                        formatter={(v) =>
                          section === "ventas"
                            ? `$${v.toLocaleString("en-US", { minimumFractionDigits: 2 })}`
                            : v.toLocaleString()
                        }
                        contentStyle={{
                          backgroundColor: "#1f324f",
                          border: "none",
                          borderRadius: 8,
                        }}
                        itemStyle={{ color: "#f0f5ff" }}
                      />
                      <Bar dataKey="value">
                        {accumData.map((_, i) => (
                          <Cell key={i} fill={COLORS[i % 2]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                );
              })()}
            </div>
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="ventas container-fluid py-4">
      <div className="header">
        <h2 className="mb-1">
          Comparativo {PREV_YEAR} vs {CUR_YEAR}
        </h2>
        <div className="legend">
          <div className="legend-item">
            <span className="dot prev" /> <strong>{PREV_YEAR}</strong>
          </div>
          <div className="legend-item">
            <span className="dot cur" /> <strong>{CUR_YEAR}</strong>
          </div>
        </div>
      </div>

      <div className="tabs mb-4">
        {["ventas", "productos"].map((t) => (
          <button
            key={t}
            className={tab === t ? "active" : ""}
            onClick={() => setTab(t)}
          >
            {t === "ventas" ? "Ventas" : "Productos"}
          </button>
        ))}
      </div>

      {renderTab(tab)}
    </div>
  );
}
